import {useEffect, useRef, useState} from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQuery} from '@tanstack/react-query';
import {
  getDefaultNetworkDetailsValues,
  getBranchFieldPath,
  getNetworkFieldPath,
  getSubBranchFieldPath,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  SelectedTreeNode,
} from './types';
import {networkDetailsSchema} from './networkDetailsSchema';
import NetworkParent from './components/NetworkParent';
import {useOnBoardingContext} from '../../../../context/onBoarding/OnBoardingContextProvider';
import NetworkSetup from './components/NetworkSetup';
import QUERY_KEYS from '../../../../constants/queryKeys';
import {
  getOnBoardingTargetSelector,
  handleGetOnBoardingDraftService,
} from '../../../../services/on-boarding';
import {
  NetworkDetailsDraftProvider,
  useNetworkDetailsDraft,
} from './NetworkDetailsDraftContext';

function getSelectedFieldPath(
  selectedNode: SelectedTreeNode | null,
): NetworkNodeFieldPath | null {
  if (!selectedNode) return null;

  switch (selectedNode.level) {
    case 'network':
      return getNetworkFieldPath(selectedNode.networkIndex);
    case 'branch':
      return getBranchFieldPath(
        selectedNode.networkIndex,
        selectedNode.branchIndex,
      );
    case 'subBranch':
      return getSubBranchFieldPath(
        selectedNode.networkIndex,
        selectedNode.branchIndex,
        selectedNode.subBranchIndex,
      );
    default:
      return null;
  }
}

function NetworkDetailsForm() {
  const hasRestoredRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedNode, setSelectedNode] = useState<SelectedTreeNode | null>({
    level: 'network',
    networkIndex: 0,
  });
  const {showSetupNetworkScreen} = useOnBoardingContext();
  const {getValues, reset} = useFormContext<NetworkDetailsFormValues>();
  const {enableAutoSave, getSavedData, setDraftBaseline} =
    useNetworkDetailsDraft();

  const {data: draftData, isLoading: isDraftLoading} = useQuery({
    queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT, getOnBoardingTargetSelector()],
    queryFn: handleGetOnBoardingDraftService,
    meta: {toastOnError: false},
  });

  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (isDraftLoading) return;

    let isCancelled = false;

    const restoreDraft = async () => {
      const savedData = await getSavedData();
      if (isCancelled || hasRestoredRef.current) return;

      const databaseValues = draftData?.data?.length
        ? {network: draftData.data}
        : getValues();

      if (savedData) {
        hasRestoredRef.current = true;
        reset(savedData);
        setDraftBaseline(databaseValues, savedData);
        enableAutoSave();
        setIsHydrated(true);
        return;
      }

      hasRestoredRef.current = true;

      if (draftData?.data?.length) {
        reset(databaseValues);
        setDraftBaseline(databaseValues);
      } else {
        setDraftBaseline(databaseValues);
      }
      enableAutoSave();
      setIsHydrated(true);
    };

    void restoreDraft();

    return () => {
      isCancelled = true;
    };
  }, [
    draftData,
    enableAutoSave,
    getValues,
    getSavedData,
    isDraftLoading,
    reset,
    setDraftBaseline,
  ]);

  const selectedFieldPath = getSelectedFieldPath(selectedNode);

  return (
    <>
      {!showSetupNetworkScreen ? (
        <NetworkParent
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          selectedFieldPath={selectedFieldPath}
          isHydrated={isHydrated}
        />
      ) : selectedFieldPath ? (
        <NetworkSetup selectedFieldPath={selectedFieldPath} />
      ) : null}
    </>
  );
}

function NetworkDetails() {
  const methods = useForm<NetworkDetailsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(networkDetailsSchema),
    defaultValues: getDefaultNetworkDetailsValues(),
  });

  return (
    <FormProvider {...methods}>
      <NetworkDetailsDraftProvider>
        <NetworkDetailsForm />
      </NetworkDetailsDraftProvider>
    </FormProvider>
  );
}

export default NetworkDetails;
